// Service Steady PWA — src/hooks/useLocation.ts — v2.0 — built 2026-06-04
//
// Persistent location state for the weather card. Resolution priority:
//   1. URL query params ?lat & ?lon & ?name (deep-link support)
//   2. localStorage 'ss-weather-location' (last picked)
//   3. Default: Wallingford, CT (the shop's location)
//
// Side effects:
//   setLocation(loc) — persists to localStorage, records in recents, fires
//     a window event so the WeatherCard's React Query refetches.
//
// Recents:
//   getRecents() returns up to 3 distinct (by name) recent picks from the
//   `ss-weather-recents` array, NOT including the current selection.

import { useCallback, useEffect, useState } from 'react';

export interface SsLocation {
  name: string;
  lat: number;
  lon: number;
  /** True when this is the shop's hardcoded default — surfaces the "shop"
   *  pin in the picker so the owner always has a one-tap reset. */
  isShopDefault?: boolean;
}

const STORAGE_KEY = 'ss-weather-location';
const RECENTS_KEY = 'ss-weather-recents';
const RECENTS_MAX = 3;

export const SHOP_DEFAULT_LOCATION: SsLocation = {
  name: 'Wallingford, CT',
  lat: 41.457,
  lon: -72.8231,
  isShopDefault: true,
};

/** Event name fired on every location change. The weather query listens
 *  and refetches. */
export const LOCATION_CHANGE_EVENT = 'ss:weather-location-changed';

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

function readStored(): SsLocation | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SsLocation>;
    if (
      typeof parsed.name === 'string' &&
      typeof parsed.lat === 'number' &&
      typeof parsed.lon === 'number'
    ) {
      return {
        name: parsed.name,
        lat: parsed.lat,
        lon: parsed.lon,
        isShopDefault: !!parsed.isShopDefault,
      };
    }
  } catch {
    /* corrupt — ignore */
  }
  return null;
}

function writeStored(loc: SsLocation): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch {
    /* private mode etc. — best effort */
  }
}

function readRecentsRaw(): SsLocation[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: SsLocation[] = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item === 'object' &&
        typeof (item as SsLocation).name === 'string' &&
        typeof (item as SsLocation).lat === 'number' &&
        typeof (item as SsLocation).lon === 'number'
      ) {
        out.push(item as SsLocation);
      }
    }
    return out;
  } catch {
    return [];
  }
}

function writeRecents(arr: SsLocation[]): void {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(arr));
  } catch {
    /* best effort */
  }
}

function recordRecent(loc: SsLocation): void {
  // Don't pollute recents with the shop default (it's always pinned at
  // the bottom of the picker via SHOP_DEFAULT_LOCATION).
  if (loc.isShopDefault) return;
  const existing = readRecentsRaw().filter((r) => r.name !== loc.name);
  existing.unshift({ name: loc.name, lat: loc.lat, lon: loc.lon });
  writeRecents(existing.slice(0, RECENTS_MAX));
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

function readUrl(): SsLocation | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const latRaw = sp.get('lat');
  const lonRaw = sp.get('lon');
  const nameRaw = sp.get('name');
  if (!latRaw || !lonRaw) return null;
  const lat = parseFloat(latRaw);
  const lon = parseFloat(lonRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return {
    name: (nameRaw ?? `${lat.toFixed(2)}, ${lon.toFixed(2)}`).slice(0, 80),
    lat,
    lon,
  };
}

// ---------------------------------------------------------------------------
// Public hook + helpers
// ---------------------------------------------------------------------------

function resolveInitial(): SsLocation {
  return readUrl() ?? readStored() ?? SHOP_DEFAULT_LOCATION;
}

export function getRecents(): SsLocation[] {
  return readRecentsRaw();
}

export function useLocation(): {
  location: SsLocation;
  setLocation: (next: SsLocation) => void;
  resetToShop: () => void;
} {
  const [location, setLocationState] = useState<SsLocation>(() => resolveInitial());

  // Re-sync on cross-tab / cross-listener events so picker → card stays in
  // step even when the actual setter is in a different component instance.
  useEffect(() => {
    const handler = (e: Event): void => {
      // 2026-06-15 — carry the new value in the event detail. "Current
      // Location" is intentionally NOT persisted, so resolveInitial() would
      // read the STALE stored/shop-default location and overwrite the just-set
      // geo coords on every instance. Prefer the detail; fall back to
      // resolveInitial() only for detail-less events (e.g. cross-tab).
      const detail = (e as CustomEvent<SsLocation | undefined>).detail;
      setLocationState(detail ?? resolveInitial());
    };
    window.addEventListener(LOCATION_CHANGE_EVENT, handler);
    return () => window.removeEventListener(LOCATION_CHANGE_EVENT, handler);
  }, []);

  const setLocation = useCallback((next: SsLocation): void => {
    // 2026-06-04 (v3.2) — don't persist placeholder names ("Current
    // Location") to localStorage or recents. The card will call
    // setLocation again once the Worker reverse-geocode resolves the
    // real "City, ST", and that's the value worth keeping.
    const placeholder = /^\s*(?:current location|)\s*$/i.test(next.name);
    if (!placeholder) {
      writeStored(next);
      recordRecent(next);
    }
    setLocationState(next);
    // 2026-06-15 — carry `next` in the event so the placeholder "Current
    // Location" (never persisted) propagates to every useLocation instance
    // instead of each one reverting to stale storage via resolveInitial().
    window.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT, { detail: next }));
  }, []);

  const resetToShop = useCallback((): void => {
    setLocation(SHOP_DEFAULT_LOCATION);
  }, [setLocation]);

  return { location, setLocation, resetToShop };
}
